/**
 * Defines a container that places its children within TabContainers which are in turn placed within a TileContainer.
 * Among its features is the ability to drag children by their tabs from one tile to another.  It also manages the
 * redistribution of children when the tiling changes.
 *
 * This container may have limited value on its own but provides the core capability needed to build the DocumentContainer.
 * See the DocumentContainer for further details.
 *
 * @module MW/layout/TileTabContainer
 * @copyright Copyright 2015-2017 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/_base/event",
    "dojo/_base/lang",
    "dojo/aspect",
    "dojo/dom-class",
    "dojo/dom-construct",
    "dojo/dom-geometry",
    "dojo/dom-style",
    "dojo/on",
    "dojo/Stateful",
    "dojo/touch",
    "MW/layout/Geometry",
    "MW/layout/LayoutChildProperty",
    "MW/layout/LayoutContainerEvent",
    "MW/layout/LayoutUtils",
    "MW/layout/SelectionBlocker",
    "MW/layout/TabContainer",
    "MW/layout/TileContainer",
    "mw-log/Log"
], function (declare, event, lang, aspect, domClass, domConstruct, domGeometry, domStyle, on, Stateful, touch,
             Geometry, LayoutChildProperty, LayoutContainerEvent, LayoutUtils, SelectionBlocker, TabContainer, TileContainer, Log) {

    var MOVE_THRESHOLD = 10;

    /**
     * @class MW/layout/TileTabContainer
     * @classdesc Defines a TileContainer subclass that places its children in TabContainers within the tiles rather than
     *            directly in the tiles.
     */
    var TileTabContainer = declare([TileContainer], {

        animationEnabled: true,
        showSingleChildTab: true,
        tileByDragEnabled: true,
        dragBetweenTilesEnabled: true,
        userTilingEnabled: true,
        _tabContainerCount: 0,
        _tileUpdateDepth: 0,

        tabContainerClass: TabContainer,

        constructor: function() {
            this._widgets = {};
            this._properties = {};
        },

        postMixInProperties: function () {
            this.offerSplitterRemoveButtons = this.userTilingEnabled;
            this.inherited(arguments);
            var defaultTabProperties = {
                tabPosition: "top",
                isContentBorderVisible: false,
                tabReorderingEnabled: true,
                tabsRepositioningEnabled: true,
                tabWidthAdjustmentEnabled: true,
                plusDropDownEnabled: false,
                selectionHighlightingEnabled: true,
                tabAnimationEnabled: true
            };
            var property;
            if (!this.tabProperties) {
                this.tabProperties = defaultTabProperties;
            } else {
                // Mix in non-conflicting defaults
                for (property in defaultTabProperties) {
                    if (this.tabProperties[property] === undefined) {
                        this.tabProperties[property] = defaultTabProperties[property];
                    }
                }
            }
        },

        postCreate: function () {
            var that = this;
            this.inherited(arguments);

            // Add tab containers to each tile
            this._addTabContainers();

            this.watch("animationEnabled", function (name, oldValue, newValue) {
                this._getTabContainers().forEach(function (tabContainer) {
                    tabContainer.set("tabAnimationEnabled", newValue);
                });
            });

            if (!this.showSingleChildTab) {
                // Configured to hide single document tabs but show such tabs anyway if the container has been split
                // and user tiling control is permitted
                this.watch(TileContainer.GRID_SIZE, function (name, oldSize, newSize) {
                    that._getTabContainers().forEach(function (tabContainer) {
                        tabContainer.set(TabContainer.HIDE_SINGLE_TAB, that._tileCount === 1 || !that.userTilingEnabled);
                    });
                });
            }
        },


        /**
         * Adds a child to the container.  The child will be managed in accordance with the provided properties.
         * If the properties object is Stateful then the container will update the values of properties designated
         * as "dynamic" below.  It will also respond to changes made to these properties.
         *
         * @param widget {_Widget} the child to add
         * @param properties {Object} an object containing any of the following properties:
         * @param properties.title {string} - Displayed on the tab corresponding to the child (this property is dynamic)
         * @param properties.tile {string} - The tile into which the child will be added (this property is dynamic)
         * @param properties.closable {boolean} - used by hosting TabContainer
         * @param properties.canClose {function} - used by hosting TabContainer
         * @param tile {number} the tile in which to place the child (optional)
         * @instance
         */
        addWithProperties: function (widget, properties, tile) {
            if (this._isInternalAdd) {
                // Add directly to TileContainer
                this.inherited(arguments);
            } else {
                this._bindChildProperties(widget, properties);
                this.addChild(widget, tile);
            }
        },

        /**
         * This is an alternative to the addWithProperties function above.  It assumes any relevant properties have
         * been added directly to the widget object.
         *
         * This method overrides _LayoutWidget.addChild
         *
         * @param widget {_Widget} the child to add
         * @param tile {number} the tile in which to place the child (optional)
         * @instance
         */
        addChild: function (widget, tile) {
            var properties, restoreTile, childAdded;
            if (this._isInternalAdd) {
                // Add directly to TileContainer
                this.inherited(arguments);
            } else {
                // Add into a tab container
                properties = this._properties[widget.id];
                if (!properties) {
                    properties = widget;
                    this._bindChildProperties(widget, properties);
                }

                Log.assert(properties.title, "No title specified for " + widget.id);

                // Default closable to true
                if (properties.closable === undefined) {
                    properties.closable = true;
                }

                if (tile === undefined) {
                    tile = -1;
                }
                if (this._pendingState) {
                    restoreTile = this._getTileForChildFromState(this._pendingState, properties.childId || properties.title);
                    if (restoreTile >= 0) {
                        tile = restoreTile;
                    } else {
                        if (tile >= 0 && tile < this._tileCount) {
                            // Add the child to the targeted tile before ending restore, lest end restore remove the tile
                            this._addToTabContainer(tile, widget, properties, undefined, true, this._isAddingBack === true);
                            childAdded = true;
                        }
                        this.endStateRestore();
                    }
                }
                if (!childAdded) {
                    if (tile < 0) {
                        tile = (properties.tile === undefined || properties.tile < 0) ? this._chooseTile() : properties.tile;
                    }
                    tile = Math.max(0, Math.min(tile, this._tileCount - 1));
                    this._addToTabContainer(tile, widget, properties, undefined, true, this._isAddingBack === true);

                    if (this.isRestoringState() && !this._areAnyTabContainersRestoringState()) {
                        this.endStateRestore();
                    }
                }
                if (!this._isAddingBack) {
                    this.emit(LayoutContainerEvent.CHILD_ADDED, widget, properties);
                }
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
        },

        /**
         * This method overrides _LayoutWidget.removeChild
         *
         * @param widget {_Widget} the widget to remove
         * @instance
         */
        removeChild: function (widget) {
            if (this._isInternalRemove) {
                this.inherited(arguments);
            } else {
                // Remove widget from owning tab container
                var container = widget.getParent();
                if (container) {
                    container.removeChild(widget, this._isTemporaryRemove === true);
                    // Further action occurs in _handleChildRemove
                }
            }
        },

        /**
         * Overridden to remove grand children rather than the tab containers holding them
         */
        removeAll: function (destroy) {
            this._getTabContainers().forEach(function (tabContainer) {
                tabContainer.removeAll(destroy);
            });
        },

        /**
         * @returns {boolean} true if the container has no children
         * @instance
         */
        isEmpty: function () {
            var empty = true;
            var tabContainers = this._getTabContainers();
            var i;
            for (i = 0; i < tabContainers.length && empty; i++) {
                empty = tabContainers[i].isEmpty();
            }
            return empty;
        },

        /**
         * @returns {number} the number of tiles that contain no children
         * @instance
         */
        getEmptyTileCount: function () {
            var count = 0;
            var tabContainers = this._getTabContainers();
            var i;
            for (i = 0; i < tabContainers.length; i++) {
                if (tabContainers[i].isEmpty()) {
                    count++;
                }
            }
            return count;
        },

        /**
         * @param predicate a function that accepts a widget and properties and returns true of the widget should be
         *                  included in the returned array
         * @returns {array} An array containing the grand children satisfying the predicate.  If no predicate is provided the
         *                  all grand children are returned.
         * @instance
         */
        getGrandChildren: function (predicate) {
            var children = [];
            var tabContainers = this._getTabContainers();
            var that = this;
            var i;
            for (i = 0; i < tabContainers.length; i++) {
                tabContainers[i].getChildrenInTabOrder().forEach(function (child) {
                    if (!predicate || predicate(child, that._getProperties(child))) {
                        children.push(child);
                    }
                })
            }
            return children;
        },

        getGrandChildCount: function () {
            var count = 0;
            var tabContainers = this._getTabContainers();
            var i;
            for (i = 0; i < tabContainers.length; i++) {
                count += tabContainers[i].getChildren().length;
            }
            return count;
        },

        eliminateEmptyTiles: function () {
            var tile, containers, removedContainer;
            var anyEliminated = true;
            while (anyEliminated) {
                anyEliminated = false;
                containers = this.getChildrenInTileOrder();
                if (containers.length > 1) {
                    for (tile = 0; tile < containers.length; tile++) {
                        if (containers[tile].isEmpty()) {
                            removedContainer = this.eliminateTile(tile);
                            if (removedContainer) {
                                removedContainer.destroyRecursive();
                                anyEliminated = true;
                                break;
                            }
                        }
                    }
                }
            }
        },

        _bindChildProperties: function (widget, properties) {
            var watches = [];
            if (this._isInternalAdd) {
                this.inherited(arguments);
                return;
            }

            this._widgets[widget.id] = widget;
            this._properties[widget.id] = properties;

            // Check for existence of watch rather than instanceof Stateful since the later check may fail if
            // properties are used across window contexts.
            if (properties.watch && properties.set && properties.get) {
                this._addChildWatches(widget, properties, watches);
            }

            var unbindingKey = this._getUnbindingKey(widget);
            properties[unbindingKey] = function () {
                watches.forEach(function(watch) {
                    watch.remove();
                });
                watches = null;
                delete properties[unbindingKey];
            };
        },

        _unbindChildProperties: function (widget, properties) {
            var unbindKey = this._getUnbindingKey(widget);
            if (properties[unbindKey]) {
                properties[unbindKey]();
            }
        },

        _addChildWatches: function (widget, properties, watches) {
            var that = this;
            var tileWatch = properties.watch(LayoutChildProperty.TILE, function (name, oldValue, newValue) {
                if (that._tileUpdateDepth) {
                    return;
                }
                var currentTile = that._getTileContainingChild(widget);
                if (newValue < 0 || newValue >= that._tileCount) {
                    that._tileUpdateDepth++;
                    properties.set(LayoutChildProperty.TILE, currentTile);
                    that._tileUpdateDepth--;
                } else  if (newValue !== currentTile) {
                    // Move the child.  Show it, i.e. select its tab if it is currently showing or if
                    // the destination tile is currently empty.
                    var show = properties.get(LayoutChildProperty.IS_SHOWING) || that.getChildInTile(newValue).isEmpty();
                    that._suppressEmptyElimination = true;
                    that._moveChild(widget, oldValue, newValue, show);
                    that._suppressEmptyElimination = false;
                }
            });
            watches.push(tileWatch);
        },

        _getUnbindingKey: function (widget) {
            return "_" + this.id + widget.id + "Unbind";
        },

        _createTabContainer: function () {
            var that = this;
            this.tabProperties.id = this.id + "TabContainer" + this._tabContainerCount++;
            this.tabProperties.hideSingleTab = !this.showSingleChildTab && (this._tileCount === 1 || !this.userTilingEnabled);
            var container = new this.tabContainerClass(this.tabProperties);

            // Aspect around remove so that the selection state prior to remove can be captured.
            container.own(aspect.around(container, 'removeChild', function (originalRemoveChild) {
                return function (widget, immediate) {
                    // See if the child being removed was selected
                    var properties = that._properties[widget.id];
                    var wasSelected = properties.get(LayoutChildProperty.IS_SELECTED);
                    originalRemoveChild.call(this, widget, immediate || !that._interactiveClose, false);
                    that._handleChildRemove(that.getTileContaining(container), widget, properties, wasSelected);
                };
            }));
            // Aspect around close so that interactive close will be animated
            container.own(aspect.around(container, "closeChild", function (originalCloseChild) {
                return function (document, force, interactive) {
                    that._interactiveClose = interactive;
                    originalCloseChild.call(this, document, force, interactive, false);
                    delete that._interactiveClose;
                };
            }));
            container.own(container.on(LayoutContainerEvent.STATE_CHANGED, function () {
                that.emit(LayoutContainerEvent.STATE_CHANGED);
            }));

            if (this.userTilingEnabled && this.dragBetweenTilesEnabled) {
                container.own(container.on(LayoutContainerEvent.DRAG_OUT, lang.hitch(this, "_beginChildDrag", container)));
            }
            return container;
        },

        // Choose the first tile whose tab container is empty. If none is empty choose the last tile.
        _chooseTile: function () {
            var tile;
            var containers = this.getChildrenInTileOrder();
            for (tile = 0; tile < this._tileCount - 1; tile++) {
                if (containers[tile].isEmpty()) {
                    break;
                }
            }
            return tile;
        },

        _destroyTabContainer: function (container) {
            container.destroyRecursive();
        },

        _addTabContainer: function (container, tile) {
            this._isInternalAdd = true;
            this.addWithProperties(container, new Stateful({
                tile: tile,
                minimumWidth: 100,
                minimumHeight: 100}));
            this._isInternalAdd = false;
        },

        _removeTabContainer: function (tile) {
            this._isInternalRemove = true;

            // Save all scroll positions
            var that = this;
            var tabContainer = this.getChildInTile(tile);
            tabContainer.getChildren().forEach(function (child) {
                LayoutUtils.saveScrollPosition(child, that._getProperties(child));
            });
            var removed = this.removeChildInTile(tile, false);

            this._isInternalRemove = false;
            return removed;
        },

        _addToTabContainer: function (tile, widget, properties, insertIndex, show, immediate) {
            var container = this.getChildInTile(tile);
            Log.assert (container, "No tab container in tile");
            if (properties !== widget) {
                container.addWithProperties(widget, properties, insertIndex, show, immediate);
            } else {
                container.addChild(widget, insertIndex, show, immediate);
            }
            if (properties.tile !== tile) {
                this._tileUpdateDepth++; // Prevent tile property watch from reacting
                properties.set(LayoutChildProperty.TILE, tile);
                this._tileUpdateDepth--;
            }
            if (show !== false) {
                setTimeout(lang.partial(LayoutUtils.restoreScrollPosition, widget, properties), 0);
            }
            // TODO: else need to restore when shown
        },

        _getProperties: function (widget) {
            return this._properties[widget.id];
        },

        _getTabContainers: function () {
            return this.getChildrenInTileOrder();
        },

        _getTileContainingChild: function (widget) {
            var container = widget.getParent();
            return container ? this.getTileContaining(container) : -1;
        },

        _updateTiles: function () {
            var that = this;
            var tile, tabContainer;
            this._tileUpdateDepth++;
            for (tile = 0; tile < this._tileCount; tile++) {
                tabContainer = this.getChildInTile(tile);
                if (tabContainer && !tabContainer.isEmpty()) {
                    tabContainer.getChildrenInTabOrder().forEach(function (child){
                        that._getProperties(child).set(LayoutChildProperty.TILE, tile);
                    });
                }
            }
            this._tileUpdateDepth--;
        },

        _removeChildrenFromTabContainer: function (tabContainer, removedChildren, predicate) {
            var that = this;
            this._isTemporaryRemove = true;
            tabContainer.setSuppressSuccession(true);
            tabContainer.getChildrenInTabOrder().forEach (function (child) {
                that._removeChildFromTabContainer(child, tabContainer, removedChildren, predicate);
            });
            tabContainer.setSuppressSuccession(false);
            this._isTemporaryRemove = false;
        },

        _removeChildFromTabContainer: function (child, tabContainer, removedChildren, predicate) {
            var childInfo = {
                widget: child,
                properties: this._getProperties(child)
            };
            if (!predicate || predicate(childInfo.widget, childInfo.properties)) {
                removedChildren.push(childInfo);
                LayoutUtils.saveScrollPosition(childInfo.widget, childInfo.properties);
                tabContainer.removeChild(child, true /* immediate */);
            }
        },

        _handleChildRemove: function (tile, widget, properties, wasSelected) {
            var removedContainer;
            if (!this._isTemporaryRemove && !this._destroyed && !this._beingDestroyed) {
                if (this.getChildInTile(tile).isEmpty()) {
                    if (this._shouldEliminateEmptyTile()) {
                        removedContainer = this.eliminateTile(tile);
                        if (removedContainer) {
                            removedContainer.destroyRecursive();
                        }
                    }
                }
                this._unbindChildProperties(widget, properties);
                delete this._widgets[widget.id];
                delete this._properties[widget.id];
                this.emit(LayoutContainerEvent.CHILD_REMOVED, widget, properties);
                this.emit(LayoutContainerEvent.STATE_CHANGED);
            }
        },

        _shouldEliminateEmptyTile: function () {
            return this.tileByDragEnabled && !this._suppressEmptyElimination &&
                ((this._tileCount <= 3 && !this._childDragInfo) ||
                   // Eliminate empty tile following drag into a non-empty tile or drag which creates a new tile
                   (this._childDragInfo && (this.getChildInTile(this._childDragInfo.tile).getChildrenInTabOrder().length > 1 ||
                                            this._tileCount > this._childDragInfo.initialTileCount)));
        },

        _prepareForGridSizeChange: function (newGridSize) {
            // Remove the children from their tab containers unless the number of tiles is 2
            // and will remain so.
            var that = this;
            var saveInfo = {
                tabContainerInfo: this._children,
                childInfo: []
            };
            var newTileCount = newGridSize.w * newGridSize.h;
            var tabContainer, showingChild, children, i;
            if (this._tileCount !== 2 || newTileCount !== 2) {
                if (this._tileCount === 1 && newTileCount === 2) {
                    // If we are going from one tile to 2 use the currently showing child as the split point
                    tabContainer = saveInfo.tabContainerInfo[0].widget;
                    showingChild = tabContainer.get(TabContainer.SHOWING_CHILD);
                    if (showingChild) {
                        saveInfo.splitIndex = Math.max(1, that._getProperties(showingChild).get(LayoutChildProperty.INDEX));
                        // Only remove children after the split point
                        this._isTemporaryRemove = true;
                        children = tabContainer.getChildrenInTabOrder();
                        for (i = saveInfo.splitIndex; i < children.length; i++) {
                            that._removeChildFromTabContainer(children[i], tabContainer, saveInfo.childInfo);
                        }
                        this._isTemporaryRemove = false;
                    }
                }
                if (saveInfo.splitIndex === undefined) {
                    this._isTemporaryRemove = true;
                    saveInfo.tabContainerInfo.forEach(function (tabContainerInfo) {
                        if (tabContainerInfo) {
                            that._removeChildrenFromTabContainer(tabContainerInfo.widget, saveInfo.childInfo);
                        }
                    });
                    this._isTemporaryRemove = false;
                }
            }
            return saveInfo;
        },

        _reactToGridSizeChange: function (savedInfo) {
            // Reuse, create or dispose tab containers as needed
            var tile, containerInfo;
            this._addTabContainers();

            // Remove and dispose any excess tab containers
            for (tile = this._tileCount; tile < savedInfo.tabContainerInfo.length; tile++) {
                containerInfo = savedInfo.tabContainerInfo[tile];
                if (containerInfo && containerInfo.widget.domNode) {
                    if (containerInfo.widget.getParent()) {
                        this._superRemoveChild(containerInfo.widget);
                    }
                    containerInfo.widget.destroyRecursive();
                }
            }
            this._distributeChildrenFollowingGridSizeChange(savedInfo);
        },

        _distributeChildrenFollowingGridSizeChange: function (savedInfo) {
            // Add grandchildren, one to each tile/tab container with any left over going to the last.
            // The one per tile distribution does not apply in the special case of a change from one tile
            // to two.
            var that = this;
            var tile = savedInfo.splitIndex ? 1 : 0;
            var count  = 0;
            var countInTile = 0;
            savedInfo.childInfo.forEach (function (childInfo) {
                if (childInfo) {
                    delete childInfo.properties.index;
                    that._addToTabContainer(tile, childInfo.widget, childInfo.properties, undefined, !countInTile, true);
                    countInTile++;
                    count++;
                    if (tile < that._tileCount - 1) {
                        tile++;
                        countInTile = 0;
                    }
                }
            });
        },

        _prepareForMerge: function (tileToKeep, tilesToRemove) {
            // Remove children from tiles being removed
            // Also save the tab containers from these tiles for eventual destruction
            var that = this;
            var mergeData = {
                removedTabContainers: [],
                removedChildren: []
            };
            tilesToRemove.forEach(function (tile) {
                var tabContainer = that.getChildInTile(tile);
                mergeData.removedTabContainers.push(tabContainer);
                that._removeChildrenFromTabContainer(tabContainer, mergeData.removedChildren);
            });
            return mergeData;
        },

        _reactToMerge: function (tileToKeep, tilesToRemove, mergeData) {
            // Add children back to the tile being kept
            // Order based on tile order, don't show unless kept tile is empty
            var that = this;
            var keptContainer = this.getChildInTile(tileToKeep);
            var show = keptContainer.isEmpty();
            mergeData.removedChildren.forEach(function (childInfo) {
                delete childInfo.properties.index;
                that._addToTabContainer(tileToKeep, childInfo.widget, childInfo.properties, undefined, show, true);
                show = false;
            });
            this._updateTiles();

            // Destroy removed tab containers
            mergeData.removedTabContainers.forEach(function (tabContainer) {
                tabContainer.destroyRecursive();
            });
        },

        _reactToSplit: function (splitTile, splitAlong) {
            var splitCellBox, newTile, tabContainer, showingChild, index, children, first;
            if (!this._suppressRedistribution) {
                splitCellBox = this._tileCellBox[splitTile];
                if (splitAlong === "vertical") {
                    newTile = this._tileInCell[splitCellBox.t + 1][splitCellBox.l];
                } else {
                    newTile = this._tileInCell[splitCellBox.t][splitCellBox.l + 1];
                }

                this._addTabContainer(this._createTabContainer(), newTile);

                // Move children to new tile
                tabContainer = this.getChildInTile(splitTile);
                showingChild = tabContainer.get(TabContainer.SHOWING_CHILD);
                children = tabContainer.getChildrenInTabOrder();
                index = Math.max(1, children.indexOf(showingChild));
                first = true;
                for (; index < children.length; index++) {
                    this._moveChild(children[index], splitTile, newTile, first);
                    first = false;
                }
            }
            this._updateTiles();
        },

        _beginChildDrag: function (tabContainer, eventData) {
            var dragChildren =  eventData.setId ? tabContainer.getSetMembersInTabOrder(eventData.setId) : [eventData.widget];
            if (this.getGrandChildren().length > dragChildren.length || this._tileCount > 1) {
                this._childDragInfo = {
                    children: dragChildren,
                    initialTileCount: this._tileCount,
                    sourceContainer: tabContainer,
                    startTile: this.getTileContaining(tabContainer),
                    startX : eventData.mousePoint.x,
                    startY : eventData.mousePoint.y,
                    connections: [on(this.ownerDocument, touch.move, lang.hitch(this, "_continueChildDrag")),
                                  on(this.ownerDocument, touch.release, lang.hitch(this, "_endChildDrag"))]
                };
                SelectionBlocker.blockUntilRelease(this.domNode);
            }
        },

        _continueChildDrag: function (e) {
            var dragInfo = this._childDragInfo;
            var offsets, dragX, dragY, cell, tile, tileBox, tabContainer, tabChildren, tabBar, tabBox, border, targetBox, half;
            if (!dragInfo.hasMoved) {
                if (Math.abs(e.pageY - dragInfo.startY) >= MOVE_THRESHOLD) {
                    dragInfo.hasMoved = true;
                    offsets = domGeometry.position(this.domNode);
                    dragInfo.pageXOffset = offsets.x;
                    dragInfo.pageYOffset = offsets.y;
                }
            }
            if (dragInfo.hasMoved) {
                // Determine tile containing mouse pointer
                dragX = e.pageX - dragInfo.pageXOffset;
                dragY = e.pageY - dragInfo.pageYOffset;
                cell = this._getCellFromPoint(dragX, dragY);
                if (cell) {
                    tile = this._tileInCell[cell.row][cell.column];
                    tileBox = this.getTilePixelBox(tile);
                    // Determine which area of the tile box is being pointed to
                    // If the child being drags is the only occupant of the tile then it can only appear in the center
                    // A drag over the tab area should also be interpreted as targeting the center
                    tabContainer = this.getChildInTile(tile);
                    tabChildren = tabContainer.getChildrenInTabOrder();
                    tabBar = tabContainer.getTabBar();
                    tabBox = domGeometry.getMarginBox(tabBar.domNode);
                    tabBox.l += tileBox.l;
                    tabBox.t += tileBox.t;
                    if (tabContainer.isEmpty() || !this.tileByDragEnabled ||
                        (tabChildren.length === 1 && tabChildren[0] === dragInfo.children[0]) ||
                        (this.restrictToSingleSplit && this._tileCount > 1 && dragInfo.sourceContainer.getTabCount() > dragInfo.children.length) ||
                        Geometry.boxContains(tabBox, dragX, dragY)) {
                        border = "center";
                    } else {
                        border = Geometry.getNearestBorder(tileBox, dragX, dragY, 0.25);
                        // If dragging all children from a tile to an adjacent tile, exclude a split that will simply
                        // duplicate the current arrangement (see geck 1504766).
                        var startContainer = this.getChildInTile(dragInfo.startTile);
                        if (startContainer.getTabCount() === dragInfo.children.length) {
                            var adjacency = this.getAdjacency(dragInfo.startTile, tile);
                            if (adjacency === border || adjacency === Geometry.oppositeRegion(border)) {
                                border = "center";
                            }
                        }
                    }
                    if (tile !== dragInfo.tile || border !== dragInfo.border) {
                        dragInfo.tile = tile;
                        dragInfo.border = border;
                        // Calculate the target indicator rectangle
                        targetBox = lang.clone(tileBox);
                        if (border === "top" || border === "bottom") {
                            half = targetBox.h / 2;
                            targetBox.h = half;
                            if (border === "bottom") {
                                targetBox.t += half;
                            }
                        } else if (border === "left" || border === "right") {
                            half = targetBox.w / 2;
                            targetBox.w = half;
                            if (border === "right") {
                                targetBox.l += half;
                            }
                        }
                        // Display target indicator
                        if (!dragInfo.targetIndicator) {
                            this._addDropTargetIndicator(dragInfo);
                            domStyle.set(dragInfo.targetIndicator, {display: "block"});
                        }
                        domGeometry.setMarginBox(dragInfo.targetIndicator, targetBox);
                    }
                }
            }
        },

        _endChildDrag: function (e) {
            var that = this;
            var dragInfo = this._childDragInfo;
            var orderedChildren, i, child, container, show;
            if (dragInfo.hasMoved) {
                if (dragInfo.startTile !== dragInfo.tile || dragInfo.border !== "center") {
                    if (dragInfo.border !== "center") {
                        // Split the tile as necessary
                        dragInfo.tile = this._splitTile(dragInfo.tile, dragInfo.border);
                        dragInfo.startTile = this._getTileContainingChild(dragInfo.children[0]);
                    }
                }
                show = true;
                if (dragInfo.startTile !== dragInfo.tile) {
                    if (dragInfo.children.length > 1) {
                        show = false;  // Only want to show the most recently selected among the moved children
                        orderedChildren = this.getChildInTile(dragInfo.startTile).getChildrenInShowOrder();
                    }

                    dragInfo.children.forEach(function (child) {
                        that._moveChild(child, dragInfo.startTile, dragInfo.tile, show);
                    });
                    // Show the most recently shown among the moved children
                    if (dragInfo.children.length > 1) {
                        container = dragInfo.children[0].getParent();
                        for (i = orderedChildren.length - 1; i >= 0; i--) {
                            child = orderedChildren[i];
                            if (dragInfo.children.indexOf(child) >= 0) {
                                container.showChild(child);
                                break;
                            }
                        }
                    }
                }
                this._removeDropTargetIndicator(dragInfo);
            }
            dragInfo.connections.forEach(function (connection) {
                connection.remove();
            });
            delete this._childDragInfo;
        },

        _splitTile: function (tileToSplit, targetBorder) {
            var newTile, targetTile;
            var splitCellBox = this._tileCellBox[tileToSplit];
            this._suppressRedistribution = true;
            if (targetBorder === "top" || targetBorder === "bottom") {
                this.splitTopBottom(splitCellBox.t, splitCellBox.l);
                newTile = this._tileInCell[splitCellBox.t + 1][splitCellBox.l];
            } else {
                this.splitLeftRight(splitCellBox.t, splitCellBox.l);
                newTile = this._tileInCell[splitCellBox.t][splitCellBox.l + 1];
            }
            this._suppressRedistribution = false;
            if (targetBorder === "top" || targetBorder === "left") {
                this._addTabContainer(this._removeTabContainer(tileToSplit), newTile);
                this._addTabContainer(this._createTabContainer(), tileToSplit);
                targetTile = tileToSplit;
            } else {
                this._addTabContainer(this._createTabContainer(), newTile);
                targetTile = newTile;
            }
            return targetTile;
        },

        _addDropTargetIndicator: function (dragInfo) {
            dragInfo.targetIndicator = domConstruct.create("div", {id : this.domNode.id + "DropIndicatorBox"}, this.domNode);
            domClass.add(dragInfo.targetIndicator, "dropIndicatorBox");
        },

        _removeDropTargetIndicator: function (dragInfo) {
            if (dragInfo.targetIndicator) {
                domConstruct.destroy(dragInfo.targetIndicator);
                delete dragInfo.targetIndicator;
            }
        },

        _moveChild: function (widget, fromTile, toTile, show) {
            var fromContainer = this.getChildInTile(fromTile);
            var properties = this._getProperties(widget);
            var removedContainer;
            this._isTemporaryRemove = true;
            LayoutUtils.saveScrollPosition(widget, properties);
            fromContainer.removeChild(widget, true /* immediate */);
            properties.set(LayoutChildProperty.INDEX, undefined);  // Clear the tab index
            this._isTemporaryRemove = false;
            this._addToTabContainer(toTile, widget, properties, undefined, show, true);
            if (fromContainer.isEmpty() && this._shouldEliminateEmptyTile()) {
                removedContainer = this.eliminateTile(fromTile, toTile);
                if (removedContainer) {
                    removedContainer.destroyRecursive();
                }
            }
        },

        getState: function () {
            var tile;
            var state = this.inherited(arguments);
            state.tileOccupancy = [];
            state.tileOccupancy.length = this._tileCount;
            for (tile = 0; tile < this._tileCount; tile++) {
                state.tileOccupancy[tile] = this.getChildInTile(tile).getState();
            }
            return state;
        },

        setState: function (state) {
            var that = this;
            var i, childIdOrTitle, childInfo, tile;

            // Remember the current tab containers
            this._pendingState = state;  // Causes isRestoringState to return true
            var saveContainers = this._getTabContainers();

            // Remove children from tab containers
            var saveChildInfo = [];
            var leftOverChildInfo = [];
            saveContainers.forEach(function (tabContainer) {
                that._removeChildrenFromTabContainer(tabContainer, saveChildInfo);
            });

            this.inherited(arguments);

            // Set the hide single tab property of existing tab containers based on the tiling (see geck 1680895)
            if (!this.showSingleChildTab) {
                this._getTabContainers().forEach(function (tabContainer) {
                    tabContainer.set(TabContainer.HIDE_SINGLE_TAB, that._tileCount === 1 || !that.userTilingEnabled);
                });
            }

            // Add tab containers to any tiles that are empty and set each tab container's state
            this._addTabContainers();
            var tileStates = state.tileOccupancy || state.tileStates;
            if (tileStates) {
                for (tile = 0; tile < Math.min(this._tileCount, tileStates.length); tile++) {
                    this.getChildInTile(tile).setState(tileStates[tile]);
                }
            }

            // Destroy any left over containers
            saveContainers.forEach(function (tabContainer) {
                if (!tabContainer.getParent()) {
                    tabContainer.destroyRecursive();

                }
            });

            // Put existing children into the tiles specified by the state
            for (i = 0; i < saveChildInfo.length; i++) {
                childInfo = saveChildInfo[i];
                childIdOrTitle = childInfo.properties.childId || childInfo.properties.title;
                tile = this._getTileForChildFromState(state, childIdOrTitle);
                if (tile >= 0) {
                    this._addToTabContainer(tile, childInfo.widget, childInfo.properties, undefined, true, true);
                } else {
                    leftOverChildInfo.push(childInfo);
                }
            }

            // If any children are left over add them back and end the restore process
            if (leftOverChildInfo.length) {
                for (i = 0; i < leftOverChildInfo.length; i++) {
                    childInfo = leftOverChildInfo[i];
                    tile = this._chooseTile();
                    this._addToTabContainer(tile, childInfo.widget, childInfo.properties, undefined, true, true);
                }
                this.endStateRestore();  // Ends for each tab container
            }

            if (!this._areAnyTabContainersRestoringState()) {
                delete this._pendingState;
            }
        },

        isRestoringState: function () {
            return !!this._pendingState;
        },

        endStateRestore: function() {
            delete this._pendingState;
            var tile;
            for (tile = 0; tile < this._tileCount; tile++) {
                this.getChildInTile(tile).endStateRestore();
            }
        },

        _getTileForChildFromState: function (state, childIdOrTitle) {
            var tile;
            var tileStates = state.tileOccupancy || state.tileStates;
            for (tile = 0; tile < tileStates.length; tile++) {
                if (TabContainer.stateIncludes(tileStates[tile], childIdOrTitle)) {
                    return tile;
                }
            }
            return -1;
        },

        _areAnyTabContainersRestoringState: function () {
            var tile;
            for (tile = 0; tile < this._tileCount; tile++) {
                if (this.getChildInTile(tile).isRestoringState()) {
                    return true;
                }
            }
            return false;
        },

        _addTabContainers: function () {
            var tile;
            for (tile = 0; tile < this._tileCount; tile++) {
                if (!this._children[tile]) {
                    this._addTabContainer(this._createTabContainer(), tile);
                }
            }
        },

        eliminateTile: function () {
            var result = this.inherited(arguments);
            if (result) {
                this._updateTiles();
            }
            return result;
        },

        /*
         * Suppress tab-container related add and remove events
         */
        emit: function(type, widget, properties) {
            var parent;
            if ((type !== LayoutContainerEvent.CHILD_ADDED && type !== LayoutContainerEvent.CHILD_REMOVED) ||
                !(widget && widget.isInstanceOf(TabContainer))) {
                this.inherited(arguments);
            }
        }
    });

    /**
     * Determines if two states are similar.  State are considered similar if both the tiling and occupancy
     * are similar.  The former is determined by the TileContainer method.  Occupancy is considered similar
     * if all of the children in the first state appear in the same tiles of the second state.  Order within
     * the tile need not match.  Furthermore the second state may include additional children and still be
     * considered similar.
     *
     * @param {Object} state1
     * @param {Object} state2
     * @return {Boolean} true if the given states are similar
     */
    TileTabContainer.areStatesSimilar = function (state1, state2) {
        if (!TileContainer.areStatesSimilar(state1, state2)) {
            return false;
        }
        var tile, children1, children2, index1, index2, id1, id2;
        for (tile = 0; tile < state1.tileOccupancy.length; tile++) {
            children1 = state1.tileOccupancy[tile].children;
            children2 = state2.tileOccupancy[tile].children;
            for (index1 = 0; index1 < children1.length; index1++) {
                id1 = children1[index1].id || children1[index1].childId;
                for (index2 = 0; index2 < children2.length; index2++) {
                    id2 = children2[index2].id || children2[index2].childId;
                    if (id1 === id2) {
                        break;
                    }
                }
                if (index2 === children2.length) {
                    return false;
                }
            }
        }
        return true;
    };

    TileTabContainer.validateState = function (state) {
        var result = TileContainer.validateState(state);
        if (result.isValid && state.tileOccupancy) {
            // Could also ask TabContainer to validate each occupancy entry.
            if (state.tileOccupancy.length > state.tileCount) {
                result.isValid = false;
                result.failureMessage = "More tileOccupancy entries than tiles";
            }
        }
        return result;
    };

    return TileTabContainer;
});
